const DEFAULT_PARTICIPANT_IMPORT_MAX_ROWS = 500;
let warnedInvalidMaxRows = false;

export function getParticipantImportMaxRows(): number {
  const raw = process.env.PARTICIPANT_IMPORT_MAX_ROWS;
  if (raw === undefined || raw.trim() === "") {
    return DEFAULT_PARTICIPANT_IMPORT_MAX_ROWS;
  }
  const configured = Number(raw);
  if (Number.isInteger(configured) && configured > 0) return configured;

  if (!warnedInvalidMaxRows) {
    warnedInvalidMaxRows = true;
    console.warn(
      `Invalid PARTICIPANT_IMPORT_MAX_ROWS; using ${DEFAULT_PARTICIPANT_IMPORT_MAX_ROWS}`
    );
  }
  return DEFAULT_PARTICIPANT_IMPORT_MAX_ROWS;
}

export function normalizeParticipantImportEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function normalizeQrReferenceNumber(
  value: string | null | undefined
): string {
  return value?.trim() ?? "";
}

export function findDuplicateQrReferenceNumbers(
  values: Array<string | null | undefined>
): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const value of values.map(normalizeQrReferenceNumber)) {
    if (!value) continue;
    if (seen.has(value)) duplicates.add(value);
    seen.add(value);
  }
  return duplicates;
}

export function buildQrReferenceAvailabilityFilter(
  eventId: string,
  value: string | null | undefined,
  excludeProfileId?: string
) {
  const qrReferenceNumber = normalizeQrReferenceNumber(value);
  if (!qrReferenceNumber) return null;
  return {
    eventId,
    qrReferenceNumber,
    ...(excludeProfileId ? { id: { not: excludeProfileId } } : {}),
  };
}

export function findDuplicateParticipantImportEmails(
  emails: string[]
): Set<string> {
  return findDuplicateNormalizedValues(emails);
}

export function findDuplicateNormalizedValues(values: string[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const value of values.map(normalizeParticipantImportEmail)) {
    if (!value) continue;
    if (seen.has(value)) duplicates.add(value);
    seen.add(value);
  }
  return duplicates;
}

export function isParticipantImportReadyToComplete(
  status: string,
  processedRows: number,
  totalRows: number
): boolean {
  return status === "PROCESSING" && processedRows === totalRows;
}

export function isLiveParticipantImportQueueState(state: string): boolean {
  return (
    state === "waiting" ||
    state === "active" ||
    state === "delayed" ||
    state === "paused" ||
    state === "prioritized" ||
    state === "waiting-children"
  );
}

export function isDeadParticipantImportQueueState(state: string): boolean {
  return state === "failed" || state === "stuck" || state === "completed";
}

// ponytail: enqueue is milliseconds; leftovers are hours. 2m covers Redis
// visibility lag. Shorten if a sweeper marks lost jobs faster.
export const PARTICIPANT_IMPORT_ENQUEUE_GRACE_MS = 120_000;

// ponytail: a live chunk can sit between writes for minutes. 10m matches the
// import queue lockDuration; shorten if progress heartbeats land more often.
export const PARTICIPANT_IMPORT_STALE_PROCESSING_MS = 600_000;

const toTime = (value: Date | number) =>
  value instanceof Date ? value.getTime() : value;

export function isBlockingParticipantImport(
  status: string,
  queueState: string,
  observedAt?: Date | number,
  now = Date.now()
): boolean {
  if (status === "PROCESSING" || status === "ROLLING_BACK") {
    if (isLiveParticipantImportQueueState(queueState)) return true;
    if (isDeadParticipantImportQueueState(queueState)) return false;
    return (
      observedAt === undefined ||
      now - toTime(observedAt) < PARTICIPANT_IMPORT_STALE_PROCESSING_MS
    );
  }
  if (status !== "PENDING") return false;
  if (isLiveParticipantImportQueueState(queueState)) return true;
  return (
    queueState === "unknown" &&
    observedAt !== undefined &&
    now - toTime(observedAt) < PARTICIPANT_IMPORT_ENQUEUE_GRACE_MS
  );
}

export function shouldRecoverDeadParticipantImport(
  status: string,
  queueState: string,
  observedAt?: Date | number,
  now = Date.now()
): boolean {
  if (status === "ROLLBACK_FAILED") return true;
  if (status !== "PROCESSING" && status !== "ROLLING_BACK") return false;
  if (isDeadParticipantImportQueueState(queueState)) return true;
  return (
    queueState === "unknown" &&
    observedAt !== undefined &&
    now - toTime(observedAt) >= PARTICIPANT_IMPORT_STALE_PROCESSING_MS
  );
}

export function handlerLeaseAllowsRollback(
  currentToken: string | null | undefined,
  providedToken?: string
): boolean {
  return !currentToken || currentToken === providedToken;
}

export function shouldAbandonPendingParticipantImport(
  status: string,
  queueState: string,
  createdAt: Date | number,
  now = Date.now()
): boolean {
  if (status !== "PENDING") return false;
  if (queueState === "failed" || queueState === "stuck") return true;
  return (
    queueState === "unknown" &&
    now - toTime(createdAt) >= PARTICIPANT_IMPORT_ENQUEUE_GRACE_MS
  );
}

const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

export function isParticipantImportObjectId(id: string): boolean {
  return OBJECT_ID_RE.test(id);
}

export function participantImportCreatorIds(session: {
  user?: { id?: string | null };
  sub?: string | null;
}): string[] {
  return [
    ...new Set(
      [session.user?.id, session.sub].filter(
        (id): id is string =>
          typeof id === "string" && isParticipantImportObjectId(id)
      )
    ),
  ];
}
